// Integer-cent cadence conversion and exact financial summaries.
import { ANNUAL_CHARGE_COUNTS } from './cadence';
import type { TuitionInvoiceItem } from './tuition';

export function dollarsToCents(amount: number | string | null | undefined) {
  const value = String(amount).trim();
  const match = /^(-?)(\d+)(?:\.(\d{1,2}))?$/.exec(value);
  if (!match) {
    throw new Error('Recurring charge amount must be representable in whole cents.');
  }
  const [, sign, whole, fraction = ''] = match;
  const cents = Number(whole) * 100 + Number(fraction.padEnd(2, '0'));
  if (!Number.isSafeInteger(cents)) throw new Error('Recurring charge amount exceeds the supported range.');
  return sign === '-' ? -cents : cents;
}

export function calculateCadenceTransitionTotal(invoiceItems: TuitionInvoiceItem[]) {
  const totalCents = invoiceItems.reduce((invoiceTotal, invoiceItem) => {
    const itemCents = dollarsToCents(invoiceItem.amount);
    const discountCents = (invoiceItem.discounts ?? []).reduce((discountTotal, discount) => {
      const amount =
        discount.amountType === 'percent'
          ? calculatePercentageDiscountCents(itemCents, decimalToRational(discount.amount))
          : dollarsToCents(discount.amount);
      const nextDiscountTotal = discountTotal + amount;
      if (!Number.isSafeInteger(nextDiscountTotal))
        throw new Error('Recurring discount total exceeds the supported range.');
      return nextDiscountTotal;
    }, 0);
    const nextInvoiceTotal = invoiceTotal + itemCents - discountCents;
    if (!Number.isSafeInteger(nextInvoiceTotal))
      throw new Error('Recurring invoice total exceeds the supported range.');
    return nextInvoiceTotal;
  }, 0);

  return totalCents / 100;
}

function roundRationalHalfAwayFromZero(numerator: number, denominator: number) {
  const sign = numerator < 0 ? -1 : 1;
  const absoluteNumerator = Math.abs(numerator);
  const quotient = Math.floor(absoluteNumerator / denominator);
  const remainder = absoluteNumerator % denominator;
  const result = sign * (quotient + (remainder >= denominator / 2 ? 1 : 0));
  return result === 0 ? 0 : result;
}

function decimalToRational(value: number | string | null | undefined) {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(String(value).trim());
  if (!match) throw new Error('Percentage discount must be a finite decimal number.');
  const [, sign, whole, fraction = ''] = match;
  const denominator = 10 ** fraction.length;
  const numerator = Number(whole) * denominator + Number(fraction);
  if (!Number.isSafeInteger(numerator) || !Number.isSafeInteger(denominator)) {
    throw new Error('Percentage discount exceeds the supported range.');
  }
  return { numerator: sign === '-' ? -numerator : numerator, denominator };
}

export function isValidPercentageDiscountAmount(value: number | string | null | undefined) {
  try {
    decimalToRational(value);
    return true;
  } catch {
    return false;
  }
}

export type CadenceTransitionFinancialError = {
  type: 'interval' | 'money' | 'percentage';
  row: TuitionInvoiceItem;
  rowIndex: number;
  discountIndex?: number;
  // 'baseline' marks an error on the persisted plan rows rather than the editable draft;
  // the provider cannot remediate it by editing, so the save must stay blocked.
  source?: 'draft' | 'baseline';
};

export function validateCadenceTransitionFinancials(
  invoiceItems: TuitionInvoiceItem[],
  previousBillingInterval?: string,
  billingInterval?: string
): CadenceTransitionFinancialError | null {
  if (previousBillingInterval && billingInterval) {
    const previousCount = ANNUAL_CHARGE_COUNTS[previousBillingInterval.toLowerCase()];
    const nextCount = ANNUAL_CHARGE_COUNTS[billingInterval.toLowerCase()];
    if (previousCount == null || nextCount == null) {
      return { type: 'interval', row: invoiceItems[0] ?? {}, rowIndex: 0 };
    }
  }
  for (let rowIndex = 0; rowIndex < invoiceItems.length; rowIndex += 1) {
    const row = invoiceItems[rowIndex];
    try {
      dollarsToCents(row.amount);
    } catch {
      return { type: 'money', row, rowIndex };
    }
    const discounts = row.discounts ?? [];
    for (let discountIndex = 0; discountIndex < discounts.length; discountIndex += 1) {
      const discount = discounts[discountIndex];
      if (discount.amountType === 'percent') {
        if (!isValidPercentageDiscountAmount(discount.amount)) {
          return { type: 'percentage', row, rowIndex, discountIndex };
        }
        continue;
      }
      try {
        dollarsToCents(discount.amount);
      } catch {
        return { type: 'money', row, rowIndex, discountIndex };
      }
    }
    try {
      calculateInvoiceGrossAndNetCents([row]);
      if (previousBillingInterval && billingInterval) {
        const converted = convertRecurringItemsForCadence([row], previousBillingInterval, billingInterval);
        buildCadenceFinancialSummary([row], converted, previousBillingInterval, billingInterval);
      }
    } catch {
      const percentageIndex = (row.discounts ?? []).findIndex((discount) => discount.amountType === 'percent');
      return percentageIndex >= 0
        ? { type: 'percentage', row, rowIndex, discountIndex: percentageIndex }
        : { type: 'money', row, rowIndex };
    }
  }
  try {
    calculateCadenceTransitionTotal(invoiceItems);
    calculateInvoiceGrossAndNetCents(invoiceItems);
  } catch {
    return { type: 'money', row: invoiceItems[0], rowIndex: 0 };
  }
  return null;
}

function safeAdd(left: number, right: number, message: string) {
  const result = left + right;
  if (!Number.isSafeInteger(result)) throw new Error(message);
  return result;
}

function safeSubtract(left: number, right: number, message: string) {
  const result = left - right;
  if (!Number.isSafeInteger(result)) throw new Error(message);
  return result;
}

function safeMultiply(left: number, right: number, message: string) {
  const result = left * right;
  if (!Number.isSafeInteger(result)) throw new Error(message);
  return result;
}

function calculatePercentageDiscountCents(amountCents: number, percentage: { numerator: number; denominator: number }) {
  const numerator = safeMultiply(amountCents, percentage.numerator, 'Percentage discount exceeds the supported range.');
  const denominator = safeMultiply(percentage.denominator, 100, 'Percentage discount exceeds the supported range.');
  return roundRationalHalfAwayFromZero(numerator, denominator);
}

function convertCentsForChargeCounts(cents: number, previousCount: number, nextCount: number) {
  const annualCents = safeMultiply(cents, previousCount, 'Recurring annual amount exceeds the supported range.');
  return roundRationalHalfAwayFromZero(annualCents, nextCount);
}

export function convertCadenceCents(cents: number, previousBillingInterval?: string, billingInterval?: string) {
  const previousCount = ANNUAL_CHARGE_COUNTS[previousBillingInterval?.toLowerCase() ?? ''];
  const nextCount = ANNUAL_CHARGE_COUNTS[billingInterval?.toLowerCase() ?? ''];
  if (previousCount == null || nextCount == null) {
    throw new Error('Recurring plan has an unsupported billing interval.');
  }
  return convertCentsForChargeCounts(cents, previousCount, nextCount);
}

export function convertRecurringItemsForCadence<T extends TuitionInvoiceItem>(
  invoiceItems: T[],
  previousBillingInterval?: string,
  billingInterval?: string
): T[] {
  const previousCount = ANNUAL_CHARGE_COUNTS[previousBillingInterval?.toLowerCase() ?? ''];
  const nextCount = ANNUAL_CHARGE_COUNTS[billingInterval?.toLowerCase() ?? ''];
  if (previousCount == null || nextCount == null)
    throw new Error('Recurring plan has an unsupported billing interval.');

  return invoiceItems.map((invoiceItem) => {
    const amountCents = dollarsToCents(invoiceItem.amount);
    const discounts = invoiceItem.discounts?.map((discount) => {
      if (discount.amountType === 'percent') return discount;
      const discountCents = dollarsToCents(discount.amount);
      return {
        ...discount,
        amount: convertCentsForChargeCounts(discountCents, previousCount, nextCount) / 100,
      };
    });

    return {
      ...invoiceItem,
      amount: convertCadenceCents(amountCents, previousBillingInterval, billingInterval) / 100,
      ...(discounts ? { discounts } : {}),
    };
  });
}

export type CadenceFinancialSummary = {
  after: { annualGross: number; annualNet: number; perInvoiceGross: number; perInvoiceNet: number };
  before: { annualGross: number; annualNet: number; perInvoiceGross: number; perInvoiceNet: number };
  annualGrossDelta: number;
  annualNetDelta: number;
  components: Array<{ id: string; label: string; annualDrift: number; tolerance: number; withinTolerance: boolean }>;
  roundingPreserved: boolean;
};

export function buildCadenceFinancialSummary(
  beforeItems: TuitionInvoiceItem[],
  afterItems: TuitionInvoiceItem[],
  previousBillingInterval?: string,
  billingInterval?: string
): CadenceFinancialSummary | null {
  const previousCount = ANNUAL_CHARGE_COUNTS[previousBillingInterval?.toLowerCase() ?? ''];
  const nextCount = ANNUAL_CHARGE_COUNTS[billingInterval?.toLowerCase() ?? ''];
  if (previousCount == null || nextCount == null) return null;

  const before = calculateInvoiceGrossAndNetCents(beforeItems);
  const after = calculateInvoiceGrossAndNetCents(afterItems);
  const components = buildFixedComponentDrifts(beforeItems, afterItems, previousCount, nextCount);
  const beforeAnnualGross = safeMultiply(
    before.gross,
    previousCount,
    'Recurring annual gross exceeds the supported range.'
  );
  const beforeAnnualNet = safeMultiply(before.net, previousCount, 'Recurring annual net exceeds the supported range.');
  const afterAnnualGross = safeMultiply(after.gross, nextCount, 'Recurring annual gross exceeds the supported range.');
  const afterAnnualNet = safeMultiply(after.net, nextCount, 'Recurring annual net exceeds the supported range.');
  return {
    before: {
      perInvoiceGross: before.gross / 100,
      perInvoiceNet: before.net / 100,
      annualGross: beforeAnnualGross / 100,
      annualNet: beforeAnnualNet / 100,
    },
    after: {
      perInvoiceGross: after.gross / 100,
      perInvoiceNet: after.net / 100,
      annualGross: afterAnnualGross / 100,
      annualNet: afterAnnualNet / 100,
    },
    annualGrossDelta:
      safeSubtract(afterAnnualGross, beforeAnnualGross, 'Recurring annual delta exceeds the supported range.') / 100,
    annualNetDelta:
      safeSubtract(afterAnnualNet, beforeAnnualNet, 'Recurring annual delta exceeds the supported range.') / 100,
    components,
    roundingPreserved: components.every((component) => component.withinTolerance),
  };
}

function buildFixedComponentDrifts(beforeItems, afterItems, previousCount, nextCount) {
  const tolerance = nextCount / 200;
  return beforeItems.flatMap((beforeItem, itemIndex) => {
    const afterItem = afterItems.find((item) => item.id && item.id === beforeItem.id) ?? afterItems[itemIndex];
    if (!afterItem) return [];
    const amountDrift =
      safeSubtract(
        safeMultiply(
          dollarsToCents(afterItem.amount),
          nextCount,
          'Recurring annual component exceeds the supported range.'
        ),
        safeMultiply(
          dollarsToCents(beforeItem.amount),
          previousCount,
          'Recurring annual component exceeds the supported range.'
        ),
        'Recurring annual component delta exceeds the supported range.'
      ) / 100;
    const itemComponent = {
      id: String(beforeItem.id ?? `row-${itemIndex}`),
      label: beforeItem.item || beforeItem.description || `Row ${itemIndex + 1}`,
      annualDrift: amountDrift,
      tolerance,
      withinTolerance: Math.abs(amountDrift) <= tolerance,
    };
    const discounts = (beforeItem.discounts ?? []).flatMap((beforeDiscount, discountIndex) => {
      if (beforeDiscount.amountType === 'percent') return [];
      const afterDiscount =
        (afterItem.discounts ?? []).find((discount) => discount.id && discount.id === beforeDiscount.id) ??
        afterItem.discounts?.[discountIndex];
      if (!afterDiscount) return [];
      const annualDrift =
        safeSubtract(
          safeMultiply(
            dollarsToCents(afterDiscount.amount),
            nextCount,
            'Recurring annual discount exceeds the supported range.'
          ),
          safeMultiply(
            dollarsToCents(beforeDiscount.amount),
            previousCount,
            'Recurring annual discount exceeds the supported range.'
          ),
          'Recurring annual discount delta exceeds the supported range.'
        ) / 100;
      return [
        {
          id: String(beforeDiscount.id ?? `${itemComponent.id}-discount-${discountIndex}`),
          label: `${itemComponent.label} discount`,
          annualDrift,
          tolerance,
          withinTolerance: Math.abs(annualDrift) <= tolerance,
        },
      ];
    });
    return [itemComponent, ...discounts];
  });
}

function calculateInvoiceGrossAndNetCents(items: TuitionInvoiceItem[]) {
  let gross = 0;
  let net = 0;
  for (const item of items) {
    const amountCents = dollarsToCents(item.amount);
    gross = safeAdd(gross, amountCents, 'Recurring gross exceeds the supported range.');
    net = safeAdd(net, amountCents, 'Recurring net exceeds the supported range.');
    for (const discount of item.discounts ?? []) {
      if (discount.amountType === 'percent') {
        const percentage = decimalToRational(discount.amount);
        net = safeSubtract(
          net,
          calculatePercentageDiscountCents(amountCents, percentage),
          'Recurring net exceeds the supported range.'
        );
      } else {
        net = safeSubtract(net, dollarsToCents(discount.amount), 'Recurring net exceeds the supported range.');
      }
    }
  }
  return { gross, net };
}

export function convertBillingIntervalAmount(
  amount: number,
  previousBillingInterval?: string,
  billingInterval?: string
) {
  if (!previousBillingInterval || !billingInterval || !Number.isFinite(amount)) return null;
  try {
    return convertCadenceCents(dollarsToCents(amount), previousBillingInterval, billingInterval) / 100;
  } catch {
    return null;
  }
}
